import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import InterventionEffectTracking from '../components/dashboard/InterventionEffectTracking';
import RootCauseAnalysis from '../components/dashboard/RootCauseAnalysis';
import AIInterventionsPanel from '../components/dashboard/AIInterventionsPanel';

const { FiActivity, FiShield, FiAlertTriangle, FiUsers, FiTrendingUp, FiFilter, FiDownload, FiTarget } = FiIcons;

export const StrategicDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState([]);
  const [rcaData, setRcaData] = useState([]);
  const [aiInterventions, setAiInterventions] = useState([]);
  const [kpis, setKpis] = useState({
    ohs: 74.5,
    psychSafety: 82.1,
    stressIndex: 28.4,
    conflictRisk: 14.2
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch intervention effects (historikk)
      const { data: intData } = await supabase
        .from('intervention_effects_1715699000000')
        .select('*')
        .order('completion_date', { ascending: false });

      if (intData) setInterventions(intData);

      // Fetch Root Cause Analysis data
      const { data: rcaResults } = await supabase
        .from('root_cause_analyses_1715700000000')
        .select('*')
        .order('created_at', { ascending: false });

      if (rcaResults) setRcaData(rcaResults);

      // Fetch AI Recommended Interventions
      const { data: aiResults } = await supabase
        .from('ai_interventions_1715701000000')
        .select('*')
        .order('priority', { ascending: false });

      if (aiResults) setAiInterventions(aiResults);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    { title: 'Org. Health Score', value: kpis.ohs, unit: '%', icon: FiActivity, color: 'indigo', trend: '+2.4%' },
    { title: 'Psykologisk Trygghet', value: kpis.psychSafety, unit: '%', icon: FiShield, color: 'emerald', trend: '+5.1%' },
    { title: 'Stress-indeks', value: kpis.stressIndex, unit: '%', icon: FiAlertTriangle, color: 'rose', trend: '-1.2%' },
    { title: 'Konfliktrisiko', value: kpis.conflictRisk, unit: '%', icon: FiUsers, color: 'amber', trend: '-0.8%' }
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Strategisk Dashbord</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Sanntidsinnsikt i virksomhetens psykososiale helsetilstand.</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 transition-colors">
            <SafeIcon icon={FiFilter} /> Filtrer
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-shadow shadow-sm">
            <SafeIcon icon={FiDownload} /> Eksporter Rapport
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi, idx) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm"
          >
            <div className="flex items-start justify-between">
              <div className={`p-3 bg-${kpi.color}-100 dark:bg-${kpi.color}-900/30 rounded-lg`}>
                <SafeIcon icon={kpi.icon} className={`w-6 h-6 text-${kpi.color}-600 dark:text-${kpi.color}-400`} />
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${kpi.trend.startsWith('+') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                {kpi.trend}
              </span>
            </div>
            <div className="mt-4">
              <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">{kpi.title}</h3>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-2xl font-bold text-slate-900 dark:text-white">{kpi.value}</span>
                <span className="text-slate-400 font-medium">{kpi.unit}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* AI Interventions Panel - New Priority Section */}
      <div className="w-full">
        <AIInterventionsPanel interventions={aiInterventions} />
      </div>

      {/* Root Cause Analysis Panel */}
      <div className="w-full">
        <RootCauseAnalysis analyses={rcaData} />
      </div>

      {/* Intervention Effect Tracking */}
      <div className="w-full">
        <InterventionEffectTracking interventions={interventions} />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 h-80 flex items-center justify-center">
          <div className="text-center space-y-2">
            <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-slate-200 mx-auto" />
            <p className="text-slate-400 text-sm">Trendanalyse (ECharts Modul)</p>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 h-80 flex items-center justify-center">
          <div className="text-center space-y-2">
            <SafeIcon icon={FiTarget} className="w-12 h-12 text-slate-200 mx-auto" />
            <p className="text-slate-400 text-sm">Avdelingsvis Sammenligning</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicDashboard;